<template>
  <button class="Cart" :class="[{ hover }, platform]">
    <div class="CartIcon">
      <Cart class="icon" />
      <CartHover class="hover-icon" />
    </div>
    <div v-if="quantity > 0" class="CartQuantity" :class="{ overflowing }">
      <span class="quantity">
        {{ displayQuantity }}
      </span>
    </div>
    <p class="CartPrice">
      {{ displayPrice }}円
    </p>
  </button>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import { Cart, CartHover } from '@ka-nabellinc/ka-nabell-icons';

export default defineComponent({
  components: {
    Cart,
    CartHover,
  },
  props: {
    platform: {
      type: String as () => 'common' | 'buy' | 'sell',
      default: 'common',
    },
    price: {
      type: Number,
      default: 0,
    },
    hover: {
      type: Boolean,
      default: false,
    },
    quantity: {
      type: Number,
      default: 0,
    },
  },
  setup(props) {
    const MAXIMUM = 99

    const displayPrice = computed(() => {
      if (props.price < 0) {
        return '0'
      } else {
        return props.price.toLocaleString()
      }
    })

    const displayQuantity = computed(() => {
      if (props.quantity > MAXIMUM) {
        return `${MAXIMUM}+`
      } else {
        return String(props.quantity)
      }
    })

    const overflowing = computed(() => {
      return props.quantity > MAXIMUM
    })

    return {
      displayPrice,
      displayQuantity,
      overflowing,
    }
  }
})
</script>

<style scoped>
.Cart {
  display: flex;
  width: 57px;
  height: 56px;
  padding: 4px 8px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2px;
  cursor: pointer;
  background: transparent;
  border: none;
  position: relative;
}

.Cart .CartIcon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  flex-basis: 24px;
}

.Cart .CartIcon > * {
  width: 100%;
  height: 100%;
}

.CartPrice {
  margin: 0;
  color: var(--primitive-colors-neutral-black-800);
  text-align: center;
  font-size: var(--font-size-x-small);
  font-weight: 400;
  line-height: 120%;
  white-space: nowrap;
}

.Cart.hover .CartPrice,
.Cart:hover .CartPrice {
  color: var(--primitive-colors-neutral-black-400);
}

.Cart .icon {
  display: inline-block;
}

.Cart.hover .icon,
.Cart:hover .icon {
  display: none;
}

.Cart .hover-icon {
  display: none;
}

.Cart.hover .hover-icon,
.Cart:hover .hover-icon {
  display: inline-block;
}

.CartQuantity {
  line-height: 15px;
  width: 17px;
  height: 17px;
  padding: 2px 4px;
  position: absolute;
  right: 11px;
  top: 5px;
  border-radius: 8px;
  border: 1px solid var(--primitive-colors-neutral-white-400);
  box-sizing: border-box;
}

.Cart .CartQuantity {
  background: var(--primitive-colors-neutral-black-400);
}

.Cart.sell .CartQuantity {
  background: var(--primitive-colors-gradient-pink-600);
}

.Cart.buy .CartQuantity {
  background: var(--primitive-colors-gradient-blue-600);
}

.quantity {
  display: inline-block;
  width: calc(100% - 4px);
  color: var(--primitive-colors-neutral-white-400);
  position: absolute;
  top: 0;
  left: 2px;
  text-align: center;
  font-size: 10px;
  font-weight: 200;
  letter-spacing: 0.1px;
  transform: scale(0.8);
}

.CartQuantity.overflowing {
  width: 24px;
  right: 4px;
}
</style>